import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { useTranslation } from 'react-i18next';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { useConfirm } from '../../shared/ConfirmContext';
import { getContrastColor, COLOR_PRIMARY, COLOR_SECONDARY, COLOR_BLUE } from '../../shared/colorUtils';
import './Notifications.css';

const NotificationsPage = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  const queryClient = useQueryClient();

  const getNotificationsUrl = (f) => {
    if (f === 'unread') return 'notifications/api/notifications/unread/';
    if (f === 'read') return 'notifications/api/notifications/?is_read=true&ordering=-created_at';
    return 'notifications/api/notifications/?ordering=-created_at';
  };

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', filter],
    queryFn: () => api.get(getNotificationsUrl(filter)).then(r => Array.isArray(r) ? r : (r.results || [])),
  });

  useRealtimeEvents((e) => {
    if (e.model === 'notifications.notification') queryClient.invalidateQueries({ queryKey: ['notifications'] });
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id) => api.post(`notifications/api/notifications/${id}/mark_as_read/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => api.post('notifications/api/notifications/mark_all_read/'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: (id) => api.delete(`notifications/api/notifications/${id}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAsRead = (notificationId) => markAsReadMutation.mutate(notificationId);

  const markAllAsRead = () => markAllAsReadMutation.mutate();

  const deleteNotification = async (notificationId) => {
    if (!await confirm(t('notifications.confirmDelete', 'Opravdu chcete smazat toto upozornění?'))) return;
    deleteNotificationMutation.mutate(notificationId);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('cs-CZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="page-body">
      <div className="container-xl">
        {/* Page header */}
        <div className="page-header d-print-none mb-4">
          <div className="container-xl">
            <div className="row g-2 align-items-center">
              <div className="col">
                <h2 className="page-title">{t('notifications.title')}</h2>
              </div>
              <div className="col-auto ms-auto d-print-none">
                {unreadCount > 0 && (
                  <button className="btn btn-primary" onClick={markAllAsRead}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M5 12l5 5l10 -10" />
                    </svg>
                    {t('notifications.markAllRead')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="card mb-3">
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs">
              <li className="nav-item">
                <a
                  className={`nav-link ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                  style={{ cursor: 'pointer' }}
                >
                  {t('notifications.all', 'Všechna')}
                  {filter === 'all' && notifications.length > 0 && (
                    <span className="badge ms-2" style={{ backgroundColor: COLOR_SECONDARY, color: getContrastColor(COLOR_SECONDARY) }}>{notifications.length}</span>
                  )}
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${filter === 'unread' ? 'active' : ''}`}
                  onClick={() => setFilter('unread')}
                  style={{ cursor: 'pointer' }}
                >
                  {t('notifications.unread', 'Nepřečtená')}
                  {unreadCount > 0 && (
                    <span className="badge ms-2" style={{ backgroundColor: COLOR_PRIMARY, color: getContrastColor(COLOR_PRIMARY) }}>{unreadCount}</span>
                  )}
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${filter === 'read' ? 'active' : ''}`}
                  onClick={() => setFilter('read')}
                  style={{ cursor: 'pointer' }}
                >
                  {t('notifications.read', 'Přečtená')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Notifications list */}
        <div className="card">
          <div className="card-body p-0">
            {isLoading ? (
              <CenteredLoader message={t('common.loading')} />
            ) : notifications.length === 0 ? (
              <div className="empty">
                <div className="empty-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
                    <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
                  </svg>
                </div>
                <p className="empty-title">{t('notifications.noNotifications')}</p>
                <p className="empty-subtitle text-muted">
                  {filter === 'unread'
                    ? t('notifications.noUnread', 'Žádná nepřečtená upozornění')
                    : t('notifications.emptyDescription', 'Zatím zde nejsou žádná upozornění')}
                </p>
              </div>
            ) : (
              <div className="list-group list-group-flush">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`list-group-item list-group-item-action notification-item-page ${!notification.is_read ? 'notification-unread-page' : ''}`}
                  >
                    <div className="row align-items-center">
                      <div className="col">
                        <div className="d-flex align-items-center mb-2">
                          <strong className="notification-title-page">{notification.title}</strong>
                          {!notification.is_read && (
                            <span className="badge ms-2" style={{ backgroundColor: COLOR_BLUE, color: getContrastColor(COLOR_BLUE) }}>{t('notifications.new')}</span>
                          )}
                        </div>
                        <div className="text-muted notification-message-page">{notification.message}</div>
                        <div className="text-muted small mt-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-sm me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                            <path d="M12 7v5l3 3" />
                          </svg>
                          {formatDateTime(notification.created_at)}
                        </div>
                      </div>
                      <div className="col-auto">
                        <div className="btn-list">
                          {!notification.is_read && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => markAsRead(notification.id)}
                              title={t('notifications.markRead', 'Označit jako přečtené')}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M5 12l5 5l10 -10" />
                              </svg>
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteNotification(notification.id)}
                            title={t('common.delete', 'Smazat')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                              <path d="M4 7l16 0" />
                              <path d="M10 11l0 6" />
                              <path d="M14 11l0 6" />
                              <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                              <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
